import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventIcon from "@mui/icons-material/Event";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import { generateGeneralReports } from "../../../../services/Reports/ReportsApi";
import { getActiveDailySessionData } from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";
import {fetchServiceAndTaxSettings} from "../../../../services/Settings/ServicesandTaxSettingsAndSessios/ServicesAndTaxApi";
import ReportDataTable from "./ReportDataTable";
import { reportColumnsConfig } from "./reportColumnsConfig";
import { exportReportsToExcel, exportSingleReportToExcel } from "./excelExport";
import ProfitandLossReport from "./ProfitandLossReport";
import { printGeneralReportUtils } from "./Printing/printGeneralReportUtils";


const keysToShow = [
  "invoices_reportes",
  "order_items_reportes",
  "expenese_reportes",
  "withdrawal_reportes",
  "employee_payroll_reportes",
  "deposite_reportes",
  "transfer_reportes",
  "reservation_reportes",
  "offer_orders_reportes",
  "order_logs_reportes",
  "customer_reportes",
  "users_reportes",
  "logs_items_reportes",
  "captain_order_reportes",
  "order_print_number",
  "delivery_reportes",
  // أضف تقرير الطلبات عبر الإنترنت
  "online_orders_reportes",
];

const formatToApiDate = (isoDate) => {
  if (!isoDate) return "";
  // isoDate expected "yyyy-mm-dd" -> return "yyyy/mm/dd"
  return isoDate.replace(/-/g, "/");
};

const GeneralReportsTap = () => {
  const [sessionMode, setSessionMode] = useState(""); // 'manual' or 'automatic'
  const [globalSearch, setGlobalSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [selectedReportTypes, setSelectedReportTypes] = useState(["all"]);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [expandAll, setExpandAll] = useState(true);
  const [activePeriod, setActivePeriod] = useState("");
  const [tab, setTab] = useState("general");

  // دالة لتنسيق التاريخ إلى yyyy-mm-dd
  const formatDateToInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // دالة للحصول على تاريخ اليوم
  const getTodayDates = () => {
    const today = new Date();
    return {
      from: formatDateToInput(today),
      to: formatDateToInput(today)
    };
  };

  // دالة للحصول على تاريخ أمس
  const getYesterdayDates = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      from: formatDateToInput(yesterday),
      to: formatDateToInput(yesterday)
    };
  };

  // دالة للحصول على تواريخ الأسبوع الحالي
  const getWeekDates = () => {
    const today = new Date();
    const firstDay = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // الاثنين كبداية الأسبوع
    firstDay.setDate(today.getDate() - diff);
    
    return {
      from: formatDateToInput(firstDay),
      to: formatDateToInput(today)
    };
  };

  // دالة للحصول على تواريخ الشهر الحالي
  const getMonthDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    return {
      from: formatDateToInput(firstDay),
      to: formatDateToInput(today)
    };
  };

  // عند التحميل: جلب إعدادات الخدمة وتعيين نوع اليومية ثم ضبط تواريخ الفلتر تلقائياً
  React.useEffect(() => {
    const initSessionMode = async () => {
      try {
        const settingsResp = await fetchServiceAndTaxSettings();
        const session =
          settingsResp?.data?.settings?.[0]?.session ||
          settingsResp?.session ||
          (settingsResp?.data && settingsResp.data.session) ||
          "";
        setSessionMode(session);

        const branchId = localStorage.getItem("branch_id");
        if (session === "manual") {
          if (!branchId) {
            console.warn("⚠️ لا يوجد branch_id في localStorage");
            return;
          }
          const sessResp = await getActiveDailySessionData(branchId);
          const sessDate =
            sessResp?.data?.session_date || sessResp?.session_date || "";
          if (sessDate) {
            setFromDate(sessDate);
            setToDate(sessDate);
          }
        } else {
          // automatic => استخدم تاريخ اليوم الفعلي
          const today = new Date();
          const todayStr = formatDateToInput(today);
          setFromDate(todayStr);
          setToDate(todayStr);
        }
      } catch (err) {
        console.error("❌ فشل في جلب إعدادات اليومية:", err);
      }
    };

    initSessionMode();
  }, []);

  // دالة لجلب التقرير حسب الفترة المحددة
  const fetchReportByPeriod = async (period) => {
    let dates;
    
    switch(period) {
      case 'today':
        dates = getTodayDates();
        break;
      case 'yesterday':
        dates = getYesterdayDates();
        break;
      case 'week':
        dates = getWeekDates();
        break;
      case 'month':
        dates = getMonthDates();
        break;
      default:
        return;
    }

    // استخدم التواريخ المحسوبة للفترة؛ لاحقاً المستخدم يستطيع تعديل fromDate/toDate يدوياً
    // (لا نجبر على session_date هنا — session_date كان يستخدم فقط كقيمة افتراضية عند التحميل)
 
    setFromDate(dates.from);
    setToDate(dates.to);
    setActivePeriod(period);
    
    // جلب التقرير تلقائياً
    const params = {
      from_date: formatToApiDate(dates.from),
      to_date: formatToApiDate(dates.to),
    };

    try {
      setLoading(true);
      setError("");
      setReport(null);
      const res = await generateGeneralReports(params);
      setReport(res || {});
      // فتح جميع التقارير افتراضياً عند التحميل
      const initialExpanded = {};
      keysToShow.forEach(key => {
        initialExpanded[key] = true;
      });
      setExpandedAccordions(initialExpanded);
      setExpandAll(true);
    } catch (err) {
      console.error(err);
      setError(err.message || "فشل أثناء إنشاء التقرير.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setError("");
    setReport(null);
    setActivePeriod("");

    if (!fromDate || !toDate) {
      setError("يرجى اختيار تاريخ البداية وتاريخ النهاية.");
      return;
    }

    // استخدم تواريخ fromDate/toDate الحالية (يسمح للمستخدم بتغييرها يدوياً)
    const params = {
      from_date: formatToApiDate(fromDate),
      to_date: formatToApiDate(toDate),
    };

    try {
      setLoading(true);
      const res = await generateGeneralReports(params);
      setReport(res || {});
      // فتح جميع التقارير افتراضياً عند التحميل
      const initialExpanded = {};
      keysToShow.forEach(key => {
        initialExpanded[key] = true;
      });
      setExpandedAccordions(initialExpanded);
      setExpandAll(true);
    } catch (err) {
      console.error(err);
      setError(err.message || "فشل أثناء إنشاء التقرير.");
    } finally {
      setLoading(false);
    }
  };

  // التعامل مع تغيير الاختيارات
  const handleReportTypeChange = (event) => {
    const value = event.target.value;
    
    // إذا تم اختيار "الكل"
    if (value.includes("all")) {
      // إذا كان "الكل" موجود بالفعل وتم اختيار عنصر آخر، نزيل "الكل"
      if (selectedReportTypes.includes("all") && value.length > 1) {
        setSelectedReportTypes(value.filter(v => v !== "all"));
      } else {
        // إذا تم اختيار "الكل" فقط
        setSelectedReportTypes(["all"]);
      }
    } else {
      // إذا تم اختيار جميع العناصر يدوياً، نضع "الكل"
      if (value.length === keysToShow.length) {
        setSelectedReportTypes(["all"]);
      } else if (value.length === 0) {
        // لا نسمح بعدم اختيار أي شيء
        setSelectedReportTypes(["all"]);
      } else {
        setSelectedReportTypes(value);
      }
    }
  };

  // فلترة التقارير حسب الأنواع المحددة
  const filteredKeys = selectedReportTypes.includes("all") 
    ? keysToShow 
    : keysToShow.filter(key => selectedReportTypes.includes(key));

  // بناء تقرير مُفلَّت حسب بحث المستخدم (على مستوى كل صف في كل تقرير)
  const filteredReport = useMemo(() => {
    if (!report) return null;
    if (!globalSearch || !globalSearch.trim()) return report;
    const term = globalSearch.toLowerCase();
    const out = {};
    keysToShow.forEach((key) => {
      const arr = Array.isArray(report[key]) ? report[key] : [];
      out[key] = arr.filter(item => JSON.stringify(item).toLowerCase().includes(term));
    });
    return out;
  }, [report, globalSearch]);

  // حساب إجمالي السجلات للتقارير المفلترة
  const getTotalRecords = () => {
    if (!report) return 0;
    const target = filteredReport || report;
    return filteredKeys.reduce((total, key) => {
      return total + (Array.isArray(target[key]) ? target[key].length : 0);
    }, 0);
  };

  // التحكم في فتح/إغلاق جميع التقارير
  const handleExpandCollapseAll = () => {
    const newExpandState = !expandAll;
    const newExpanded = {};
    filteredKeys.forEach(key => {
      newExpanded[key] = newExpandState;
    });
    setExpandedAccordions(newExpanded);
    setExpandAll(newExpandState);
  };

  // التحكم في فتح/إغلاق تقرير محدد
  const handleAccordionChange = (key) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [key]: isExpanded
    }));
    
    // تحديث حالة expandAll بناءً على حالة جميع التقارير
    const allExpanded = filteredKeys.every(k => 
      k === key ? isExpanded : expandedAccordions[k]
    );
    setExpandAll(allExpanded);
  };

  // دالة لتصدير جميع التقارير المفلترة
  const handleExportAll = () => {
    if (!report) {
      alert('لا توجد بيانات للتصدير');
      return;
    }

    if (filteredKeys.length === 0) {
      alert('الرجاء تحديد تقرير واحد على الأقل للتصدير');
      return;
    }

    exportReportsToExcel(report, filteredKeys, fromDate, toDate);
  };

  // دالة لطباعة التقرير العام: تبني الحزمة المفلترة وترسلها للـ util
  const handlePrintGeneral = async () => {
    if (!report) {
      alert("لا توجد بيانات للطباعة.");
      return;
    }

    const target = filteredReport || report;
    const payload = {
      meta: { from: fromDate, to: toDate, generatedAt: new Date().toISOString() },
      data: {},
      keys: filteredKeys,
    };

    filteredKeys.forEach((key) => {
      payload.data[key] = Array.isArray(target[key]) ? target[key] : [];
    });

    try {
      if (typeof printGeneralReportUtils === "function") {
        await printGeneralReportUtils(payload);
      } else {
        alert("خدمة الطباعة غير متاحة.");
      }
    } catch (err) {
      console.error("❌ فشل في إرسال بيانات الطباعة:", err);
      alert("فشل في تجهيز الطباعة.");
    }
  };

  // دالة لتصدير تقرير واحد
  const handleExportSingle = (key) => {
    if (!report || !report[key]) {
      alert('لا توجد بيانات للتصدير');
      return;
    }

    exportSingleReportToExcel(key, report[key], fromDate, toDate);
  };
  
  const handleTabChange = (e, value) => {
    setTab(value);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* تبويبات أعلى الصفحة */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
        >
          <Tab label="جميع التقارير" value="general" />
          <Tab label="تقارير أرباح وخسائر" value="profit" />
        </Tabs>
      </Box>

      {tab === "profit" ? (
        // عرض مكون تقرير الأرباح والخسائر
        <ProfitandLossReport />
      ) : (
        <>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 600
            }}
          >
            توليد تقرير عام
          </Typography>

          {/* أزرار الفترات الزمنية السريعة */}
          <Box sx={{ 
            display: "flex", 
            gap: { xs: 1, sm: 1.5 },
            mb: { xs: 2, md: 2.5 },
            flexWrap: "wrap",
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Button
              variant={activePeriod === 'today' ? "contained" : "outlined"}
              onClick={() => fetchReportByPeriod('today')}
              disabled={loading}
              startIcon={<CalendarTodayIcon />}
              sx={{ 
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.75, sm: 1 },
                flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              اليوم
            </Button>
            
            <Button
              variant={activePeriod === 'yesterday' ? "contained" : "outlined"}
              onClick={() => fetchReportByPeriod('yesterday')}
              disabled={loading}
              startIcon={<EventIcon />}
              sx={{ 
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.75, sm: 1 },
                flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              أمس
            </Button>
            
            <Button
              variant={activePeriod === 'week' ? "contained" : "outlined"}
              onClick={() => fetchReportByPeriod('week')}
              disabled={loading}
              startIcon={<DateRangeIcon />}
              sx={{ 
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.75, sm: 1 },
                flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              الأسبوع
            </Button>
            
            <Button
              variant={activePeriod === 'month' ? "contained" : "outlined"}
              onClick={() => fetchReportByPeriod('month')}
              disabled={loading}
              startIcon={<CalendarMonthIcon />}
              sx={{ 
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.75, sm: 1 },
                flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
                minWidth: { xs: 'auto', sm: 120 }
              }}
            >
              الشهر
            </Button>
          </Box>

          <Box sx={{ 
            display: "flex", 
            gap: { xs: 1, sm: 1.5, md: 2 },
            flexDirection: { xs: "column", sm: "row" }, 
            mb: { xs: 2, md: 3 },
            alignItems: { xs: 'stretch', sm: 'flex-end' }
          }}>
            <TextField
              label="من تاريخ"
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setActivePeriod("");
              }}
              InputLabelProps={{ 
                shrink: true,
                sx: { fontSize: { xs: '0.95rem', md: '1rem' } }
              }}
              inputProps={{
                sx: { fontSize: { xs: '0.95rem', md: '1rem' } }
              }}
              fullWidth
              sx={{ minWidth: { sm: 200 } }}
            />
            <TextField
              label="إلى تاريخ"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setActivePeriod("");
              }}
              InputLabelProps={{ 
                shrink: true,
                sx: { fontSize: { xs: '0.95rem', md: '1rem' } }
              }}
              inputProps={{
                sx: { fontSize: { xs: '0.95rem', md: '1rem' } }
              }}
              fullWidth
              sx={{ minWidth: { sm: 200 } }}
            />
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              sx={{ 
                whiteSpace: "nowrap",
                minHeight: { xs: 48, sm: 42 },
                fontSize: { xs: '0.95rem', md: '1rem' },
                px: { xs: 2, sm: 3 }
              }}
            >
              {loading ? <CircularProgress color="inherit" size={20} /> : "إنشاء التقرير"}
            </Button>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '0.9rem', md: '1rem' }
              }}
            >
              {error}
            </Alert>
          )}

          {report && (
            <>
              <Alert 
                severity="success" 
                sx={{ 
                  mb: { xs: 2, md: 3 },
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }}
              >
                {report.message || "تم إنشاء التقرير"}
              </Alert>

              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: { xs: 2, md: 3 },
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 500
                }}
              >
                نطاق التقرير: {report.from_date || formatToApiDate(fromDate)} — {report.to_date || formatToApiDate(toDate)}
              </Typography>

              {/* فلتر نوع التقرير */}
              <Box sx={{ 
                mb: { xs: 2, md: 3 },
                display: "flex", 
                gap: { xs: 1.5, sm: 2 },
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", lg: "row" },
                flexWrap: "wrap"
              }}>
                <FormControl sx={{ 
                  minWidth: { xs: '100%', sm: 300, lg: 350 },
                  maxWidth: { xs: '100%', lg: 500 }
                }}>
                  <InputLabel sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}>
                    اختر نوع التقرير
                  </InputLabel>
                  <Select
                    multiple
                    value={selectedReportTypes}
                    onChange={handleReportTypeChange}
                    input={<OutlinedInput label="اختر نوع التقرير" />}
                    sx={{
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      '& .MuiSelect-select': {
                        py: { xs: 1.5, sm: 1.75 }
                      }
                    }}
                    renderValue={(selected) => {
                      if (selected.includes("all")) {
                        return <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          جميع التقارير
                        </Typography>;
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const config = reportColumnsConfig[value];
                            return (
                              <Chip 
                                key={value} 
                                label={config?.title || value} 
                                size="small"
                                sx={{ 
                                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                                  height: { xs: 24, md: 28 }
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: { xs: 300, sm: 400 },
                          '& .MuiMenuItem-root': {
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            py: { xs: 1, sm: 1.5 }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={selectedReportTypes.includes("all")} />
                      <ListItemText 
                        primary="جميع التقارير"
                        primaryTypographyProps={{
                          fontSize: { xs: '0.9rem', md: '1rem' }
                        }}
                      />
                      <Chip 
                        label={keysToShow.length} 
                        size="small" 
                        color="primary" 
                        sx={{ 
                          ml: 1,
                          fontSize: { xs: '0.75rem', md: '0.8rem' }
                        }} 
                      />
                    </MenuItem>
                    
                    {keysToShow.map((key) => {
                      const config = reportColumnsConfig[key];
                      const dataLength = Array.isArray(report[key]) ? report[key].length : 0;
                      const isSelected = selectedReportTypes.includes(key) || selectedReportTypes.includes("all");
                      
                      return (
                        <MenuItem key={key} value={key}>
                          <Checkbox checked={isSelected} />
                          <ListItemText 
                            primary={config?.title || key}
                            primaryTypographyProps={{
                              fontSize: { xs: '0.9rem', md: '1rem' }
                            }}
                            sx={{ flex: 1 }}
                          />
                          <Chip 
                            label={dataLength} 
                            size="small" 
                            color={dataLength > 0 ? "success" : "default"}
                            sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {/* حقل البحث الشامل تحت فلتر نوع التقرير */}
                <Box sx={{ width: { xs: '100%', sm: 320 }, mt: { xs: 1, sm: 0 } }}>
                  <TextField
                    placeholder="بحث شامل في كل البيانات..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Box>
                
                <Box sx={{ 
                  display: "flex", 
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: "center",
                  flexWrap: "wrap",
                  width: { xs: '100%', lg: 'auto' },
                  justifyContent: { xs: 'space-between', sm: 'flex-start' }
                }}>
                  <Chip
                    label={`إجمالي السجلات: ${getTotalRecords()}`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      height: { xs: 28, md: 32 },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' }
                    }}
                  />
                  <Chip
                    label={`عدد التقارير: ${filteredKeys.length}`}
                    color="secondary"
                    variant="outlined"
                    sx={{ 
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      height: { xs: 28, md: 32 },
                      flex: { xs: '1 1 auto', sm: '0 0 auto' }
                    }}
                  />
                  
                  {/* زر فتح/إغلاق الكل */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={expandAll ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                    onClick={handleExpandCollapseAll}
                    sx={{ 
                      whiteSpace: "nowrap",
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      height: { xs: 32, md: 36 },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    {expandAll ? "إغلاق الكل" : "فتح الكل"}
                  </Button>

                  {/* زر تصدير Excel */}
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportAll}
                    sx={{ 
                      whiteSpace: "nowrap",
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      height: { xs: 32, md: 36 },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    تصدير إلى Excel
                  </Button>

                 {/* زر طباعة التقرير العام */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintGeneral}
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                      px: { xs: 1, sm: 1.5 },
                      height: { xs: 32, md: 36 },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    طباعة
                  </Button>
                </Box>
              </Box>

              {/* عرض التقارير المفلترة */}
              {filteredKeys.map((key) => {
                const config = reportColumnsConfig[key];
                const data = (filteredReport && Array.isArray(filteredReport[key]) ? filteredReport[key] : report[key]) || [];
                const dataLength = Array.isArray(data) ? data.length : 0;
                
                return (
                  <Accordion 
                    key={key}
                    expanded={expandedAccordions[key] || false}
                    onChange={handleAccordionChange(key)}
                    sx={{ 
                      mb: { xs: 1, md: 1.5 },
                      '&:before': {
                        display: 'none',
                      },
                      boxShadow: { xs: 1, sm: 2 }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: { xs: 1.5, sm: 2, md: 3 },
                        py: { xs: 1, sm: 1.5 },
                        minHeight: { xs: 56, sm: 64 },
                        '& .MuiAccordionSummary-content': {
                          my: { xs: 1, sm: 1.5 }
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: { xs: 1, sm: 1.5 },
                        width: "100%",
                        flexWrap: { xs: 'wrap', sm: 'nowrap' }
                      }}>
                        <Typography sx={{ 
                          fontWeight: 700, 
                          flex: 1,
                          fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                          lineHeight: 1.4
                        }}>
                          {config?.title || key}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip 
                            label={`${dataLength} سجل`} 
                            size="small" 
                            color={dataLength > 0 ? "success" : "default"}
                            sx={{ 
                              fontSize: { xs: '0.75rem', md: '0.8rem' },
                              height: { xs: 24, md: 28 },
                              fontWeight: 600
                            }}
                          />
                          {dataLength > 0 && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<FileDownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportSingle(key);
                              }}
                              sx={{ 
                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                py: 0.25,
                                px: { xs: 0.75, md: 1 },
                                minWidth: 'auto',
                                display: { xs: 'none', sm: 'flex' }
                              }}
                            >
                              تصدير
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ 
                       px: { xs: 0, sm: 1, md: 2 },
                       py: { xs: 1, sm: 2 }
                     }}>
                      <ReportDataTable reportKey={key} data={data} />
                      
                      {/* زر تصدير للموبايل */}
                      {dataLength > 0 && (
                        <Box sx={{ 
                          display: { xs: 'flex', sm: 'none' },
                          justifyContent: 'center',
                          mt: 2,
                          px: 2
                        }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={() => handleExportSingle(key)}
                            sx={{ fontSize: '0.85rem' }}
                          >
                            تصدير هذا التقرير إلى Excel
                          </Button>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default GeneralReportsTap;
